/**
 * The only three contracts this app talks to directly.
 *
 * Every swap goes through calldata the API built; nothing here encodes a route, a pool or
 * an amount that reaches a pool. These are the approvals in front of it and the WNat
 * wrapper beside it.
 */

export const erc20Abi = [
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [{ type: 'uint256' }],
  },
  {
    type: 'function',
    name: 'approve',
    stateMutability: 'nonpayable',
    inputs: [
      { name: 'spender', type: 'address' },
      { name: 'amount', type: 'uint256' },
    ],
    outputs: [{ type: 'bool' }],
  },
  {
    type: 'function',
    name: 'balanceOf',
    stateMutability: 'view',
    inputs: [{ name: 'account', type: 'address' }],
    outputs: [{ type: 'uint256' }],
  },
  { type: 'function', name: 'decimals', stateMutability: 'view', inputs: [], outputs: [{ type: 'uint8' }] },
  { type: 'function', name: 'symbol', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
  { type: 'function', name: 'name', stateMutability: 'view', inputs: [], outputs: [{ type: 'string' }] },
] as const

/**
 * Permit2's allowance ledger: the read half only.
 *
 * The allowance itself is never set with `Permit2.approve` from here. The user signs a
 * `PermitSingle` instead (see `lib/permit2.ts`) and `/swap` applies it inside the swap
 * transaction. This read is what decides whether a signature is needed at all, and which
 * `nonce` it must carry.
 *
 * Note `expiration`: a Permit2 allowance is not just an amount. A non-zero amount with a
 * timestamp in the past spends nothing, and the swap reverts inside the router with the
 * user's tokens still in their wallet. `useApprovals` checks both.
 */
export const permit2Abi = [
  {
    type: 'function',
    name: 'allowance',
    stateMutability: 'view',
    inputs: [
      { name: 'owner', type: 'address' },
      { name: 'token', type: 'address' },
      { name: 'spender', type: 'address' },
    ],
    outputs: [
      { name: 'amount', type: 'uint160' },
      { name: 'expiration', type: 'uint48' },
      { name: 'nonce', type: 'uint48' },
    ],
  },
] as const

/** WNat, for the native-FLR wrap panel. The router never sees native FLR from this app. */
export const wnatAbi = [
  { type: 'function', name: 'deposit', stateMutability: 'payable', inputs: [], outputs: [] },
  {
    type: 'function',
    name: 'withdraw',
    stateMutability: 'nonpayable',
    inputs: [{ name: 'amount', type: 'uint256' }],
    outputs: [],
  },
] as const

/** uint160 max — Permit2 reads this allowance amount as "never decrement". */
export const MAX_UINT160 = (1n << 160n) - 1n
